// This class acts as a factory, which returns the correct FormClass that gathers necessary plan data for a packet document
// see usage in FoxItHandlers setFieldValues() method
import { type DocumentFile, getDocumentRule } from '@/data/documentRules'
import { DocumentType } from '@/enums/DocumentType'
import type { CartItemAndLineItem } from '@/mixins/merchandise'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { ScenarioOutput } from '@precoa/calculator-sdk'

export default class FormFactory {
	public static formsNeedingScenarioOutput: DocumentType[] = [
		DocumentType.StatementOfGoodsAndServices,
		DocumentType.EnrollmentForm,
		DocumentType.PaymentAuthorization,
		DocumentType.PreneedAgreement,
		DocumentType.PreneedContractGuaranteed,
		DocumentType.PreneedContractNonGuaranteed,
	]

	static getFormClass(
		stateCode: string,
		formType: DocumentType,
		planId: string,
		formatPrice: (price: number | null | undefined) => '' | string,
		t?: (key: string) => string,
		cartItems?: CartItemAndLineItem[],
		activePriceBookVersion?: number,
		enrollmentPaymentDetails?: ScenarioOutput
	) {
		const sessionDocumentsStore = useSessionDocumentsStore()
		const sessionStore = useSessionStore()
		const documentRule = getDocumentRule(
			stateCode!,
			formType,
			sessionStore.activeFh?.corporateAffiliation ?? ''
		)

		let formClass: DocumentFile['formClass'] | undefined =
			documentRule?.formClass

		const alternateFormClass = documentRule?.alternateFormClass
		if (alternateFormClass && sessionDocumentsStore.useAlternateFormClass) {
			formClass = alternateFormClass
		}

		if (!formClass) {
			return null
		}

		// Special handling for GoodAndServicesForm which needs cart items
		if (formType === DocumentType.StatementOfGoodsAndServices) {
			return new formClass(
				planId,
				cartItems,
				formatPrice,
				t,
				activePriceBookVersion,
				enrollmentPaymentDetails
			)
		}

		if (this.formsNeedingScenarioOutput.includes(formType)) {
			return new (formClass as any)(planId, enrollmentPaymentDetails)
		}

		return new formClass(planId)
	}
}
